import { camelCase } from 'lodash';
import React, { useCallback, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  ISG_COLUMN_HEADERS,
  ISG_REPORT_VIEW_OPTIONS,
  ISG_SORT_ORDER_OPTIONS,
} from '../../../../../utils/constants';
import { COURSE_LEVEL_ACTIVITIES_UNIT_ID } from '../../constants';
import useViewportWidth from '../../hooks/useViewportWidth';
import {
  groupAssignmentsByUnit,
  groupAssignmentsByWeek,
  sortAssignments,
} from '../../utils';
import { SectionCard } from './SectionCard';
import s from './SectionCards.module.css';

export const SectionCards = ({
  currentView,
  categoriesData,
  courseData,
  // Display
  isDropdownOpen,
  // Route Params
  getLaunchUrl,
  handleLocalLaunch,
  hasStudentReport,
  getStudentReportLink,
}) => {
  const { isMobile } = useViewportWidth();
  const settings = useSelector((state) => state.gradebook.settings);

  const userIClickerProperties = useSelector(
    (state) => ({
      isReefLinked: state.user.isReefLinked,
      isReefStatusFetching: state.user.isReefStatusFetching,
      reefProfile: state.user.reefProfile,
      reefCourseName: state.user.reefCourseName,
      reefCourseId: state.user.reefCourseId,
    }),
    shallowEqual,
  );
  const gradebookUser = useSelector((state) => state.gradebook.user);
  const user = {
    ...gradebookUser,
    ...userIClickerProperties,
  };

  const currentStudent = useSelector(
    (state) => state.gradebook.individualStudentGradebookData.currentStudent,
  );

  const [sortBy, setSortBy] = useState(camelCase(ISG_COLUMN_HEADERS.DUE_DATE));
  const [sortOrder, setSortOrder] = useState(ISG_SORT_ORDER_OPTIONS.ASC);

  const handleSortClick = useCallback(
    (sortByType) => {
      // Eg. "Assignment Name" => "assignmentName"
      const sortingBy = camelCase(sortByType);
      setSortBy(sortingBy);

      if (sortingBy === sortBy) {
        // Toggle the order if the same column is clicked
        setSortOrder(
          sortOrder === ISG_SORT_ORDER_OPTIONS.ASC
            ? ISG_SORT_ORDER_OPTIONS.DESC
            : ISG_SORT_ORDER_OPTIONS.ASC,
        );
      } else {
        // Set the order to DESC if a different column is clicked
        setSortOrder(ISG_SORT_ORDER_OPTIONS.DESC);
      }
    },
    [sortBy, sortOrder],
  );

  let allSectionData;
  if (currentView === ISG_REPORT_VIEW_OPTIONS.CATEGORY) {
    allSectionData = categoriesData;
  }

  if (currentView === ISG_REPORT_VIEW_OPTIONS.WEEK) {
    const allAssignments = categoriesData.flatMap(
      (category) => category.unfilteredAssignments,
    );

    // Affected by search
    const filteredAssignmentIds = categoriesData.flatMap((category) =>
      category.assignments.flatMap((assignment) => assignment.id),
    );

    allSectionData = groupAssignmentsByWeek(
      allAssignments,
      filteredAssignmentIds,
      courseData.enrollmentStartDate,
      courseData.startWeekOn,
      settings,
    );
  }

  if (currentView === ISG_REPORT_VIEW_OPTIONS.UNIT) {
    const allAssignments = categoriesData.flatMap(
      (category) => category.unfilteredAssignments,
    );

    // Affected by search
    const filteredAssignmentIds = categoriesData.flatMap((category) =>
      category.assignments.flatMap((assignment) => assignment.id),
    );

    allSectionData = groupAssignmentsByUnit(
      allAssignments,
      filteredAssignmentIds,
      courseData.folders,
      settings,
    );
  }

  const filteredAllSectionData = allSectionData?.filter((sectionData) => {
    return (
      sectionData.assignments.length > 0 ||
      sectionData?.unfilteredAssignments?.length
    );
  });

  const getPreviousSectionData = (currentView, index, allSectionData) => {
    const supportsTrends = [
      ISG_REPORT_VIEW_OPTIONS.WEEK,
      ISG_REPORT_VIEW_OPTIONS.UNIT,
    ].includes(currentView);

    if (!supportsTrends) {
      return null;
    }

    const firstNonCourseLevelIndex = allSectionData.findIndex(
      (section) => section.id !== COURSE_LEVEL_ACTIVITIES_UNIT_ID,
    );

    if (index === 0 || index === firstNonCourseLevelIndex) {
      return null;
    }

    let previousIndex = index - 1;
    while (
      previousIndex >= 0 &&
      allSectionData[previousIndex]?.id === COURSE_LEVEL_ACTIVITIES_UNIT_ID
    ) {
      previousIndex--;
    }

    if (previousIndex >= 0) {
      return {
        total: allSectionData[previousIndex]?.total,
      };
    }

    return null;
  };

  return (
    <div className={s.sectionCards}>
      {filteredAllSectionData?.map((sectionData, index, allSectionData) => {
        const sortedAssignments = sortAssignments(
          sortBy,
          sortOrder,
          sectionData.assignments,
        );

        const previousSectionData = getPreviousSectionData(
          currentView,
          index,
          allSectionData,
        );

        return (
          <SectionCard
            key={sectionData.id}
            sectionData={sectionData}
            previousSectionData={previousSectionData}
            assignments={sortedAssignments}
            user={user}
            currentStudent={currentStudent}
            settings={settings}
            // Display
            currentView={currentView}
            isDropdownOpen={isDropdownOpen}
            isMobile={isMobile}
            // Sorting
            onSortClick={handleSortClick}
            sortBy={sortBy}
            sortOrder={sortOrder}
            // Route Params
            getLaunchUrl={getLaunchUrl}
            handleLocalLaunch={handleLocalLaunch}
            getStudentReportLink={getStudentReportLink}
            hasStudentReport={hasStudentReport}
          />
        );
      })}
    </div>
  );
};
